/**
 * SalesArgumentsPanel — Arguments commerciaux Noam pour fiches Super Partenaire.
 *
 * Doctrine 47 commerciale (V23.14.7e) : croise les méta-asymétries Noam
 * (forces structurelles) avec les besoins du partenaire (bras_arme_notes)
 * pour produire 3-5 arguments prêts à l'emploi en RDV.
 *
 * Pattern : auto-fetch latest au mount, génération à la demande, polling 2s.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Sparkles, Loader2, RefreshCw, AlertTriangle, MessageCircle, Target,
  ShieldAlert, CheckCircle2, ChevronDown, ChevronUp,
} from "lucide-react";
import FreshnessBadge from "./FreshnessBadge";

export default function SalesArgumentsPanel({ entityId, entityName }) {
  const [phase, setPhase] = useState("idle");
  const [progress, setProgress] = useState("");
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => {
    if (!entityId) return undefined;
    let cancelled = false;
    setData(null); setPhase("idle"); setError(null);
    (async () => {
      try {
        const r = await api.get(`/workspace/annuaire/entities/${entityId}/sales-arguments/latest`);
        if (cancelled) return;
        if (r.data?.available) { setData(r.data); setPhase("done"); }
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; stopPolling(); };
  }, [entityId]);

  const generate = useCallback(async () => {
    setPhase("running"); setError(null); setProgress("Démarrage…");
    try {
      const r = await api.post(`/workspace/annuaire/entities/${entityId}/sales-arguments/generate`);
      const job_id = r.data?.job_id;
      if (!job_id) throw new Error("Pas de job_id");
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get(`/workspace/annuaire/entities/sales-arguments/jobs/${job_id}`);
          const d = s.data || {};
          setProgress(d.progress || "");
          if (d.status === "done") {
            stopPolling();
            const latest = await api.get(`/workspace/annuaire/entities/${entityId}/sales-arguments/latest`);
            if (latest.data?.available) setData(latest.data);
            setPhase("done");
            notify.success("Arguments commerciaux générés.");
          } else if (d.status === "failed") {
            stopPolling(); setError(d.error || "Erreur"); setPhase("failed");
          }
        } catch (e) { stopPolling(); setError(fmtError(e)); setPhase("failed"); }
      }, 2000);
    } catch (e) { setError(fmtError(e)); setPhase("failed"); }
  }, [entityId]);

  const args = data?.arguments || [];
  const objections = data?.objections_to_prepare || [];

  return (
    <div className="border border-[#378ADD]/30 bg-gradient-to-br from-[#378ADD]/[0.06] to-transparent rounded-md p-3 space-y-3" data-testid="annuaire-sales-args-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <MessageCircle className="w-4 h-4 text-[#378ADD]" />
          <span className="text-sm font-medium text-zinc-100">Arguments commerciaux Noam</span>
          {data && (
            <FreshnessBadge
              lastGeneratedAt={data.created_at}
              moduleLabel={`Args commerciaux · ${entityName || ""}`}
              testid="annuaire-sales-args-freshness"
            />
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={generate}
            disabled={phase === "running"}
            className="text-xs px-2 py-1 rounded border border-white/10 hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1"
            data-testid="annuaire-sales-args-generate-btn"
          >
            {phase === "running" ? (
              <><Loader2 className="w-3 h-3 animate-spin" /> {progress || "Analyse…"}</>
            ) : data ? (
              <><RefreshCw className="w-3 h-3" /> Régénérer</>
            ) : (
              <><Sparkles className="w-3 h-3" /> Préparer mon RDV</>
            )}
          </button>
          {data && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="text-zinc-500 hover:text-zinc-200 p-1"
              data-testid="annuaire-sales-args-toggle"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
      </div>

      {phase === "idle" && !data && (
        <p className="text-[11px] text-zinc-400 italic">
          Noam croise ses forces structurelles avec le profil de cet acteur pour générer 3-5 arguments prêts à l'emploi en RDV.
        </p>
      )}

      {phase === "failed" && error && (
        <div className="p-2 rounded border border-[#E24B4A]/40 bg-[#E24B4A]/10 text-[11px] text-[#E24B4A]" data-testid="annuaire-sales-args-error">
          <AlertTriangle className="w-3 h-3 inline mr-1" /> {error}
        </div>
      )}

      {phase === "done" && data && expanded && (
        <div className="space-y-3" data-testid="annuaire-sales-args-results">
          {/* Question d'accroche */}
          {data.opening_question && (
            <div className="p-2 rounded bg-[#EFD127]/[0.08] border border-[#EFD127]/30">
              <div className="text-[10px] uppercase tracking-wider text-[#EFD127] mb-1">Accroche d'ouverture</div>
              <div className="text-xs text-zinc-100 italic" data-testid="annuaire-sales-args-opening">« {data.opening_question} »</div>
            </div>
          )}

          {/* Arguments */}
          {args.length > 0 && (
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Arguments à utiliser ({args.length})</div>
              {args.map((a, i) => (
                <div key={i} className="rounded p-2 bg-white/[0.03] border border-white/5" data-testid={`annuaire-sales-args-arg-${i}`}>
                  <div className="text-xs font-medium text-zinc-100">{i + 1}. {a.title}</div>
                  {a.pitch_sentence && (
                    <div className="text-[11px] text-[#378ADD] italic mt-1 border-l-2 border-[#378ADD] pl-2">
                      « {a.pitch_sentence} »
                    </div>
                  )}
                  <div className="text-[10px] text-zinc-500 mt-1 space-y-0.5">
                    {a.client_pain_addressed && <div>→ Pain client : {a.client_pain_addressed}</div>}
                    {a.proof_point && <div>→ Preuve : {a.proof_point}</div>}
                    {a.noam_force_leveraged && (
                      <div className="text-[#1D9E75]">→ Force Noam utilisée : {a.noam_force_leveraged}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Objections */}
          {objections.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                <ShieldAlert className="w-3 h-3 text-[#EF9F27]" /> Objections probables à préparer
              </div>
              {objections.map((o, i) => (
                <div key={i} className="text-[11px] p-1.5 rounded bg-[#EF9F27]/[0.05] border border-[#EF9F27]/15" data-testid={`annuaire-sales-args-objection-${i}`}>
                  <div className="text-zinc-300 font-medium">« {o.objection} »</div>
                  <div className="text-zinc-400 mt-0.5">→ {o.response}</div>
                </div>
              ))}
            </div>
          )}

          {/* Next step */}
          {data.next_step_recommendation && (
            <div className="p-2 rounded bg-[#1D9E75]/[0.06] border border-[#1D9E75]/25">
              <div className="text-[10px] uppercase tracking-wider text-[#1D9E75] flex items-center gap-1 mb-1">
                <CheckCircle2 className="w-3 h-3" /> Prochain pas à proposer en fin de RDV
              </div>
              <div className="text-[11px] text-zinc-200" data-testid="annuaire-sales-args-nextstep">
                {data.next_step_recommendation}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
